import { useEffect, useMemo, useState, type ReactNode } from "react";
import * as Tabs from "@radix-ui/react-tabs";
import * as Popover from "@radix-ui/react-popover";
import { Command } from "cmdk";
import { Check, ChevronDown, Eye, EyeOff, Loader2, Search, X } from "lucide-react";
import { formatDisplay, substituteDomainRecordTokens } from "../core";
import { useRuntime } from "../react/runtime/RuntimeContext";
import type {
    FieldDef,
    FormButtonElement,
    FormElement,
    FormFieldElement,
    FormNotebookElement,
    FormNotebookPage,
    FormPropertyElement,
    FormSectionElement,
    FormSeparatorElement,
    Json,
    RecordData,
} from "../core";
import { commandSignature } from "../core";
import { Cell } from "./Cell";
import { enumKey } from "./cell_utils";
import { confirm } from "./ConfirmDialog";
import { DecisionCard } from "./DecisionCard";
import { appIcon } from "./icons";
import { useFormStateContext } from "../react/form/RecordProvider";
import { usePendingCommand } from "../react/hooks/usePendingCommand";
import { isElementInvisible } from "./formSheetHelpers";
import { RelationListView } from "./RelationListView";
import { ImageFieldEditor, ImageTile, extractAttachmentUuid, resolveImageFieldSize } from "./fields/ImageField";
import { ChipsInput } from "../widgets/builtins";
import { CodeEditorField } from "../widgets/CodeEditor";
import { DomainSelectorEditorHost } from "../widgets/domain_selector/DomainSelectorField";
import { DateField, DateTimeField } from "../widgets/DatePicker";
import {
    modeForWidget,
    currencyDisplayFrom,
    type QualifierRecord,
} from "../widgets/qualified/transformers";

// FormSheet — recursive walker over the DSL `<sheet>` element tree.
//
// WS 4.2 scope:
//   - `<field>`        read-mode value via widget registry (edit widgets are WS 4.3)
//   - `<field widget="record_title"/>`  hoisted to the `.form-sheet__title` slot
//   - `<section>`      heading + N-column grid; nested sections render side-by-side
//   - `<notebook>`     Radix Tabs strip + panel per `<page>`
//   - `<button>`       inline action button — confirm gate + per-signature spinner
//   - `<statusbar>`    out of scope here (sheet RelaxNG forbids; renders in `<header>`)
//   - `<related>`      out of scope (rendered in FormControlPanel)
//   - `<property>`     anchored custom-property field → record[bag][key]
//
// All visibility predicates run against the form draft (the live edit buffer)
// so toggling a field updates the visibility of dependent rows in real time.

// Human-name keys, in priority order. The first non-empty string the
// draft carries becomes the source for an image field's placeholder
// initials (e.g. "Acme Corp" → "AC"). Deliberately limited to real name
// fields — coded identifiers (reference / code / id) make poor initials,
// so a record with none of these falls back to the image icon instead.
// Raw `name` outranks `display_name`: display_name_format may embed the
// record code (res.partner ships "[{code}] {name}" → "[EMP-002] Aditya
// Patel"), and initials derived from that render as "[0" instead of "AP".
const RECORD_LABEL_KEYS = ["name", "title", "display_name"];

/** Best-effort display label for the current record, drawn from the draft.
    Drives the image-field placeholder initials. */
function pickRecordLabel(draft: RecordData): string {
    for (const key of RECORD_LABEL_KEYS) {
        const v = draft[key];
        if (typeof v === "string" && v.trim()) return v.trim();
    }
    return "";
}

interface FormSheetProps {
    elements: FormElement[];
    fieldDefs: FieldDef[];
    actionSlug: string;
    modelKey: string;
    recordRef: string | null;
    onRunButton?: (button: FormButtonElement) => void;
}

export function FormSheet({
    elements,
    fieldDefs,
    actionSlug,
    modelKey,
    recordRef,
    onRunButton,
}: FormSheetProps): ReactNode {
    const { draft, isNew, isEditing, onWriteField, invalidFields } = useFormStateContext();
    const fieldDefMap = new Map(fieldDefs.map((f) => [f.name, f]));

    const ctx: RenderCtx = {
        draft,
        isNew,
        isEditing,
        onWriteField,
        invalidFields,
        fieldDefMap,
        actionSlug,
        modelKey,
        recordRef,
        onRunButton,
    };

    // `widget="record_title"` used to be hoisted out of its DSL position
    // into a dedicated `.form-sheet__title` slot at the top of the
    // sheet. Per the partner-form smoke test, the user wants the
    // title to stay where the DSL places it — inside whatever section
    // it's declared in — just at the larger record-title typography.
    // FormFieldRow handles the typography branch via the
    // `.form-field--title` modifier (see below); no hoist needed.
    return (
        <div className="form-sheet">
            <div className="form-sheet__body">
                {elements.map((el, i) => (
                    <FormElementRenderer key={i} el={el} ctx={ctx} />
                ))}
            </div>
        </div>
    );
}

interface RenderCtx {
    draft: RecordData;
    isNew: boolean;
    isEditing: boolean;
    onWriteField: (name: string, value: import("../core").Json) => void;
    invalidFields: ReadonlySet<string>;
    fieldDefMap: Map<string, FieldDef>;
    actionSlug: string;
    modelKey: string;
    recordRef: string | null;
    onRunButton?: (button: FormButtonElement) => void;
}

function FormElementRenderer({ el, ctx, inSection = false }: { el: FormElement; ctx: RenderCtx; inSection?: boolean }): ReactNode {
    if ("invisible" in el && isElementInvisible(el, ctx.draft)) return null;
    switch (el.type) {
        case "field":
            return <FormFieldRow el={el} ctx={ctx} inSection={inSection} />;
        case "property":
            return <FormPropertyRow el={el} ctx={ctx} />;
        case "section":
            return <FormSection el={el} ctx={ctx} />;
        case "notebook":
            return <FormNotebook el={el} ctx={ctx} />;
        case "separator":
            return <FormSeparator el={el} />;
        case "button":
            return <FormButton el={el} ctx={ctx} />;
        case "statusbar":
            // RelaxNG schema forbids statusbar in <sheet>; the header strip
            // owns it. Unreachable in valid DSL.
            return null;
        case "decision":
            // AI Insights deterministic decision cockpit card. Evaluates the
            // current record after load via /api/insights/evaluate.
            return <DecisionCard modelKey={ctx.modelKey} recordRef={ctx.recordRef} title={el.title} />;
        case "related":
            // The toolbar (FormControlPanel) renders `<related>` chips.
            return null;
        default:
            return null;
    }
}

function FormFieldRow({ el, ctx, inSection = false }: { el: FormFieldElement; ctx: RenderCtx; inSection?: boolean }): ReactNode {
    const fd = ctx.fieldDefMap.get(el.name);
    if (!fd) return null;
    const label = el.string ?? fd.label ?? fd.name;
    const isRequired = el.required === true || fd.required === true;

    // `nolabel`: explicit `nolabel="1"` on the DSL field. Skips rendering
    // the label cell. Image / binary fields used to auto-promote here,
    // but that conflated two concerns — "hide the label" with "stretch
    // the value cell across the whole row" — which made image tiles
    // sit inside an over-wide section with empty space around them.
    // Image fields now use the dedicated `.form-field--image` modifier
    // below; the DSL keeps its explicit `nolabel="1"` for label-hiding.
    // A field declared DIRECTLY on the sheet / notebook page (no parent
    // <section>) also auto-suppresses its label: section-less placement
    // means "give the widget the whole row" (embedded tables, notebooks'
    // relation lists) and a floating label reads as noise there
    // (UX feedback 2026-07-02).
    const nolabel = el.nolabel === true || !inSection;
    // `form-field--image`: image-specific layout. Always hides the
    // label (image fields don't carry one — the tile IS the value) and
    // overrides the nolabel grid-stretch so the tile sits at its
    // natural tile width regardless of the surrounding `cols=` grid.
    const isImageField = fd.type === "image";
    // `form-field--title`: widget="record_title" renders in place (no
    // hoist) at the larger title typography. The label sits ABOVE the
    // input box (same `.form-field__label` format as every other field,
    // just stacked vertically) — the title field becomes a full-width
    // flex-column block spanning its declared section's grid row.
    const isTitleField = el.widget === "record_title";
    // Flagged by a failed save (blank required field) — paints the label +
    // input red until the user fills it. Cleared in useFormState.onWriteField.
    const isInvalid = ctx.invalidFields.has(el.name);

    // Edit-mode rendering for the common scalar types. Complex types
    // (reference / many2many / one2many / image / binary / json / uuid)
    // still fall back to read-mode Cell until WS 4.3 lands their pickers.
    //
    // Relational collection fields (many2many / one2many) carry a kernel
    // `readonly=True` flag because they don't write a DB column directly —
    // their writes flow through tuple commands. That flag is a STORAGE
    // concern, not a UX concern: the user CAN still edit the relation via
    // the picker. Honor `fd.readonly` for SCALAR fields only; for relational
    // fields the DSL `<field readonly="true">` attribute is the sole UX gate.
    const isRelationalCollection = fd.type === "many2many" || fd.type === "one2many";
    const isReadonly = el.readonly === true || (!isRelationalCollection && fd.readonly === true);
    // When an inline `<ListView>` is declared on an M2M field, the author
    // has opted into the embedded RelationListView (full table editor)
    // instead of the default chip-picker. Suppress the inline editor so
    // the RelationListView branch wins for that field.
    const prefersRelationList = fd.type === "many2many" && el.inlineListView != null;
    // `widget="chips"` opts a relational/list field out of the embedded
    // RelationListView: read mode renders the compact `<Chips>` rail (via Cell)
    // and edit mode renders the chip picker (M2MCombobox for m2m, ChipsInput for
    // json). For m2m the inline editor already wins; for json (not in the
    // default inline-editor type set) admit it explicitly here.
    const wantsChips = el.widget === "chips";
    const chipsEditable = wantsChips && fd.type === "json";
    const showEditor = ctx.isEditing && !isReadonly && (hasInlineEditor(fd.type) || chipsEditable) && !prefersRelationList;

    const classes = [
        "form-field",
        nolabel ? "form-field--nolabel" : "",
        el.colspan && el.colspan > 1 ? "form-field--span" : "",
        isImageField ? "form-field--image" : "",
        isTitleField ? "form-field--title" : "",
        isInvalid ? "form-field--invalid" : "",
        el.cssClass ?? "",   // developer-supplied class(es) from <field class="…"/>
    ].filter(Boolean).join(" ");

    return (
        <div className={classes} data-viewid="ede-field" data-field={el.name}>
            {nolabel || isImageField ? null : (
                <span className="form-field__label">
                    {label}
                    {isRequired ? <span className="form-field__required">*</span> : null}
                </span>
            )}
            <span className="form-field__value">
                {showEditor ? (
                    <FieldEditor
                        name={el.name}
                        type={fd.type}
                        value={ctx.draft[el.name]}
                        fieldDef={fd}
                        placeholder={el.placeholder ?? undefined}
                        displayFormat={el.displayFormat ?? null}
                        domainTemplate={el.domain ?? null}
                        draft={ctx.draft}
                        onWriteField={ctx.onWriteField}
                        actionSlug={ctx.actionSlug}
                        modelKey={ctx.modelKey}
                        recordRef={ctx.recordRef}
                        label={label}
                        widget={el.widget ?? undefined}
                        widgetOptions={el.options ?? null}
                    />
                ) : isRelationalCollection && fd.targetModel && !wantsChips ? (
                    // O2M / M2M render an embedded list view of the
                    // related child records. WS 4.3 Phase 1 ships read
                    // mode; Phase 2 lights up Add Line / Remove / popup
                    // form when isEditing. Tuple commands ([[0,0,vals]] /
                    // [[1,uuid,vals]] / [[2,uuid,0]]) flow back through
                    // `onWriteField` so buildSaveValues round-trips them
                    // to the backend at parent-save time.
                    // `widget="chips"` opts out → falls through to the Cell
                    // chips rail below (read mode); edit mode used the
                    // M2MCombobox editor above.
                    <RelationListView
                        targetModel={fd.targetModel}
                        value={ctx.draft[el.name] as import("../core").Json}
                        inlineListView={el.inlineListView ?? null}
                        isEditing={ctx.isEditing}
                        onCommandsChange={(cmds) => ctx.onWriteField(el.name, cmds as import("../core").Json)}
                        removeMode={fd.type === "many2many" ? "unlink" : "delete"}
                        parentRecord={ctx.draft}
                        parentModelKey={ctx.modelKey}
                    />
                ) : fd.type === "image" ? (
                    // Read-mode image — render the thumbnail tile. The
                    // editor branch above handles the (showEditor=true)
                    // path with upload/clear controls.
                    <ImageTile
                        uuid={extractAttachmentUuid(ctx.draft[el.name])}
                        label={label}
                        size={resolveImageFieldSize(el.options?.size)}
                        fallbackText={pickRecordLabel(ctx.draft)}
                    />
                ) : (
                    <Cell
                        value={ctx.draft[el.name]}
                        widget={el.widget ?? undefined}
                        fieldDef={fd}
                        record={ctx.draft}
                        options={el.options ?? undefined}
                        displayFormat={el.displayFormat ?? null}
                    />
                )}
            </span>
        </div>
    );
}

// `<property name="<bag>:<key>"/>` (foundation.customization Phase 4) — an
// anchored custom-property field. The backend synthesizes a FieldDef named
// "<bag>:<key>"; the value lives at `record[bag][key]` (a JSONB column) rather
// than a top-level column. We adapt the property to the shared FormFieldRow by
// exposing a synthetic draft entry at the wire name and intercepting writes to
// merge them back into the bag object. An unknown / inactive property (no
// synthesized FieldDef) renders nothing — the view never breaks.
function FormPropertyRow({ el, ctx }: { el: FormPropertyElement; ctx: RenderCtx }): ReactNode {
    const fd = ctx.fieldDefMap.get(el.name);
    if (!fd) return null;

    const { bag, key } = el;
    const readBag = (d: RecordData): Record<string, Json> => {
        const raw = d[bag];
        return raw && typeof raw === "object" && !Array.isArray(raw)
            ? (raw as Record<string, Json>)
            : {};
    };

    // Expose the property's value at its wire name so FormFieldRow reads it
    // like any field; route writes back into the bag's sub-object.
    const derivedDraft: RecordData = { ...ctx.draft, [el.name]: readBag(ctx.draft)[key] ?? null };
    const wrappedWrite = (name: string, value: Json): void => {
        if (name === el.name) {
            ctx.onWriteField(bag, { ...readBag(ctx.draft), [key]: value });
        } else {
            ctx.onWriteField(name, value);
        }
    };
    const derivedCtx: RenderCtx = { ...ctx, draft: derivedDraft, onWriteField: wrappedWrite };

    const syntheticField: FormFieldElement = {
        type: "field",
        name: el.name,
        string: el.string ?? null,
        widget: el.widget ?? null,
        invisible: el.invisible ?? null,
        readonly: el.readonly,
        required: el.required ?? undefined,
        nolabel: el.nolabel,
        placeholder: el.placeholder ?? null,
        options: el.options,
    };
    return <FormFieldRow el={syntheticField} ctx={derivedCtx} />;
}

// Field types whose edit-mode rendering is handled inline. Complex types
// not in this set (one2many / image / binary / json / uuid) drop back to
// read-mode Cell — those pickers land in follow-up WS work.
const INLINE_EDITOR_TYPES = new Set([
    "char", "text", "integer", "decimal", "boolean", "enum",
    "date", "datetime", "reference", "many2many", "image",
]);
function hasInlineEditor(type: string): boolean {
    return INLINE_EDITOR_TYPES.has(type);
}

function FieldEditor({
    name,
    type,
    value,
    fieldDef,
    placeholder,
    displayFormat,
    domainTemplate,
    draft,
    onWriteField,
    actionSlug,
    modelKey,
    recordRef,
    label,
    widget,
    widgetOptions,
}: {
    name: string;
    type: string;
    value: unknown;
    fieldDef: FieldDef;
    placeholder?: string;
    displayFormat?: string | null;
    domainTemplate?: string | null;
    draft: RecordData;
    onWriteField: (name: string, value: import("../core").Json) => void;
    actionSlug: string;
    modelKey: string;
    recordRef: string | null;
    label: string;
    widget?: string;
    widgetOptions: Record<string, string> | null;
}): ReactNode {
    // domain_selector — visual EDE-domain builder (foundation.presentation
    // Enh 13). Wins before every type branch: the stored value is a domain
    // (JSON string or array), not a scalar the type editors would understand.
    if (widget === "domain_selector") {
        return (
            <DomainSelectorEditorHost
                name={name}
                value={value}
                draft={draft}
                widgetOptions={widgetOptions}
                onWriteField={onWriteField}
            />
        );
    }
    // Qualified-value widgets (measure / monetary / datetime_tz / phone) own
    // their edit chrome — a value input paired with the qualifier (symbol affix
    // or sibling-bound dropdown). Must win before the type-based branches below
    // (e.g. datetime_tz before the plain `datetime` branch). See Enhancement 12.
    if (modeForWidget(widget)) {
        return (
            <QualifiedValueEditor
                name={name}
                type={type}
                value={value}
                fieldDef={fieldDef}
                placeholder={placeholder}
                draft={draft}
                onWriteField={onWriteField}
                actionSlug={actionSlug}
                widget={widget}
                widgetOptions={widgetOptions}
            />
        );
    }
    if (type === "image") {
        // Wired to the foundation.document attachments controller.
        // Upload requires the consumer record to exist (no FK target
        // until save); the editor self-disables when recordRef is null
        // and surfaces "Save first to upload." as guidance.
        return (
            <ImageFieldEditor
                value={value}
                onChange={(next) => onWriteField(name, next)}
                modelKey={modelKey}
                recordUuid={recordRef ?? ""}
                fieldName={name}
                label={label}
                disabled={fieldDef.readonly === true}
                size={resolveImageFieldSize(widgetOptions?.size)}
                fallbackText={pickRecordLabel(draft)}
            />
        );
    }
    if (type === "reference") {
        return (
            <ReferenceCombobox
                name={name}
                value={value as import("../core").Json}
                actionSlug={actionSlug}
                fieldName={name}
                onWriteField={onWriteField}
                placeholder={placeholder ?? (fieldDef.label ? `Search ${fieldDef.label}…` : "Search…")}
                displayFormat={displayFormat}
                domainTemplate={domainTemplate}
                draft={draft}
            />
        );
    }
    if (type === "many2many") {
        return (
            <M2MCombobox
                name={name}
                value={value as import("../core").Json}
                actionSlug={actionSlug}
                fieldName={name}
                onWriteField={onWriteField}
                placeholder={placeholder ?? (fieldDef.label ? `Add ${fieldDef.label}…` : "Add…")}
                displayFormat={displayFormat}
                domainTemplate={domainTemplate}
                draft={draft}
            />
        );
    }
    if (type === "boolean") {
        return (
            <input
                type="checkbox"
                className="form-checkbox"
                checked={Boolean(value)}
                onChange={(e) => onWriteField(name, e.target.checked)}
            />
        );
    }
    if (type === "enum") {
        const enumK = enumKey(value);
        return (
            <EnumCombobox
                name={name}
                value={enumK == null ? null : String(enumK)}
                options={fieldDef.selection ?? []}
                onWriteField={onWriteField}
                placeholder={placeholder ?? "Select…"}
            />
        );
    }
    if (widget === "chips") {
        // Non-relational list field (json array / free tags). Relational
        // many2many already returned via M2MCombobox above; reference / enum /
        // boolean / image likewise — so reaching here means a scalar-ish field
        // opting into the editable chip rail.
        return (
            <ChipsInput
                value={value}
                onChange={(next) => onWriteField(name, next)}
                placeholder={placeholder ?? (fieldDef.label ? `Add ${fieldDef.label}…` : "Add…")}
            />
        );
    }
    if (widget === "password_mask" && (type === "char" || type === "text")) {
        return (
            <PasswordMaskField
                name={name}
                value={value}
                placeholder={placeholder}
                onWriteField={onWriteField}
            />
        );
    }
    if (widget === "code" && (type === "char" || type === "text")) {
        // Syntax-highlighted source editor (CodeMirror). Language from
        // `option-language` (xml | json). First consumer: ir.application.view
        // arch — the raw string round-trips; the backend hooks validate it.
        return (
            <CodeEditorField
                value={value}
                language={widgetOptions?.language}
                placeholder={placeholder}
                onChange={(next) => onWriteField(name, next === "" ? null : next)}
            />
        );
    }
    if (type === "date") {
        return (
            <DateField
                value={value}
                onChange={(next) => onWriteField(name, next)}
                disabled={fieldDef.readonly === true}
                placeholder={placeholder ?? "Select date"}
            />
        );
    }
    if (type === "datetime") {
        return (
            <DateTimeField
                value={value}
                onChange={(next) => onWriteField(name, next)}
                disabled={fieldDef.readonly === true}
                placeholder={placeholder ?? "Select date & time"}
            />
        );
    }
    const inputType =
        type === "integer" || type === "decimal" ? "number"
        : "text";
    const step = type === "decimal" ? "any" : type === "integer" ? "1" : undefined;
    const displayed = value == null ? "" : String(value);
    return (
        <input
            type={inputType}
            step={step}
            className="form-input"
            value={displayed}
            placeholder={placeholder ?? ""}
            onChange={(e) => {
                const raw = e.target.value;
                if (raw === "") { onWriteField(name, null); return; }
                if (type === "integer") {
                    const n = parseInt(raw, 10);
                    onWriteField(name, Number.isNaN(n) ? null : n);
                } else if (type === "decimal") {
                    const n = parseFloat(raw);
                    onWriteField(name, Number.isNaN(n) ? null : n);
                } else {
                    onWriteField(name, raw);
                }
            }}
        />
    );
}

/**
 * Edit-mode renderer for the qualified-value widget family (Enhancement 12).
 * Pairs a value input with its qualifier:
 *   - monetary → number input + read-only currency symbol affix (record-driven
 *     currency; position from the currency's symbol_position).
 *   - measure  → number input + sibling-bound unit dropdown (user picks the UOM).
 *   - datetime_tz → DateTimeField (edits the absolute instant) + optional
 *     sibling-bound timezone dropdown; the instant is never rewritten.
 *   - phone    → sibling-bound country dialing-code dropdown + tel input.
 * The qualifier dropdowns reuse `ReferenceCombobox`, writing the picked record
 * back to the sibling field named by `options-<x>_field`.
 */
/** Currency-precision number input: blurred → value rendered at the
    currency's decimal precision; focused → free-form editing. Writes the
    parsed number (or null) back on every change. */
function MonetaryInput({
    value,
    decimals,
    placeholder,
    onWrite,
}: {
    value: unknown;
    decimals: number;
    placeholder?: string;
    onWrite: (n: number | null) => void;
}): ReactNode {
    const [focused, setFocused] = useState(false);
    const [text, setText] = useState("");
    const formatted = value == null || value === "" || Number.isNaN(Number(value))
        ? ""
        : Number(value).toFixed(decimals);
    return (
        <input
            type="number"
            step="any"
            className="form-input"
            value={focused ? text : formatted}
            placeholder={placeholder ?? ""}
            onFocus={() => {
                setText(formatted);
                setFocused(true);
            }}
            onBlur={() => setFocused(false)}
            onChange={(e) => {
                const raw = e.target.value;
                setText(raw);
                if (raw === "") { onWrite(null); return; }
                const n = parseFloat(raw);
                onWrite(Number.isNaN(n) ? null : n);
            }}
        />
    );
}

function QualifiedValueEditor({
    name,
    type,
    value,
    fieldDef,
    placeholder,
    draft,
    onWriteField,
    actionSlug,
    widget,
    widgetOptions,
}: {
    name: string;
    type: string;
    value: unknown;
    fieldDef: FieldDef;
    placeholder?: string;
    draft: RecordData;
    onWriteField: (name: string, value: import("../core").Json) => void;
    actionSlug: string;
    widget?: string;
    widgetOptions: Record<string, string> | null;
}): ReactNode {
    const mode = modeForWidget(widget);

    const numberInput = (
        <input
            type="number"
            step="any"
            className="form-input"
            value={value == null ? "" : String(value)}
            placeholder={placeholder ?? ""}
            onChange={(e) => {
                const raw = e.target.value;
                if (raw === "") { onWriteField(name, null); return; }
                const n = parseFloat(raw);
                onWriteField(name, Number.isNaN(n) ? null : n);
            }}
        />
    );

    const siblingDropdown = (fieldName: string | undefined, ph: string): ReactNode => {
        if (!fieldName) return null;
        return (
            <span className="qualified-field__unit">
                <ReferenceCombobox
                    name={fieldName}
                    value={draft[fieldName] as import("../core").Json}
                    actionSlug={actionSlug}
                    fieldName={fieldName}
                    onWriteField={onWriteField}
                    placeholder={ph}
                    draft={draft}
                />
            </span>
        );
    };

    // monetary — symbol affix (read-only), side driven by symbol_position.
    // The affix + input render as ONE joined control (`--affixed`), and the
    // value displays at the currency's declared decimal precision while
    // blurred (650.00, not the raw scale-4 650.0000); focusing frees the
    // text for editing (UX feedback 2026-07-02).
    if (mode === "format") {
        const curField = widgetOptions?.currency_field ?? "currency_id";
        const c = currencyDisplayFrom(draft[curField] as QualifierRecord);
        const affix = c.symbol ? <span className="qualified-field__affix">{c.symbol}</span> : null;
        return (
            <span className={`qualified-field${c.symbol ? " qualified-field--affixed" : ""}`}>
                {c.position === "before" ? affix : null}
                <MonetaryInput
                    value={value}
                    decimals={c.decimals}
                    placeholder={placeholder}
                    onWrite={(n) => onWriteField(name, n)}
                />
                {c.position === "after" ? affix : null}
            </span>
        );
    }

    // measure — value + sibling unit dropdown.
    if (mode === "convert") {
        return (
            <span className="qualified-field">
                {numberInput}
                {siblingDropdown(widgetOptions?.uom_field, "Unit")}
            </span>
        );
    }

    // datetime_tz — instant editor + optional sibling timezone dropdown.
    if (mode === "reframe") {
        return (
            <span className="qualified-field">
                <DateTimeField
                    value={value}
                    onChange={(next) => onWriteField(name, next)}
                    disabled={fieldDef.readonly === true}
                    placeholder={placeholder ?? "Select date & time"}
                />
                {siblingDropdown(widgetOptions?.timezone_field, "Zone")}
            </span>
        );
    }

    // phone — leading country dialing-code dropdown + tel input.
    if (mode === "prefix") {
        return (
            <span className="qualified-field qualified-field--lead">
                {siblingDropdown(widgetOptions?.country_field, "+__")}
                <input
                    type="tel"
                    className="form-input"
                    value={value == null ? "" : String(value)}
                    placeholder={placeholder ?? ""}
                    onChange={(e) => onWriteField(name, e.target.value === "" ? null : e.target.value)}
                />
            </span>
        );
    }

    // Unreachable (modeForWidget guarded the call site) — fall back to a plain
    // input so a future mode never renders blank.
    return type === "datetime" || type === "date" ? null : numberInput;
}

/**
 * `password_mask` form editor — a controlled input that masks its value
 * (type="password") with an eye button to toggle visibility while typing.
 * Empty string writes back as null (matching the plain char editor).
 */
function PasswordMaskField({
    name,
    value,
    placeholder,
    onWriteField,
}: {
    name: string;
    value: unknown;
    placeholder?: string;
    onWriteField: (name: string, value: import("../core").Json) => void;
}): ReactNode {
    const [visible, setVisible] = useState(false);
    const displayed = value == null ? "" : String(value);
    return (
        <span className="password-mask">
            <input
                type={visible ? "text" : "password"}
                className="form-input password-mask__input"
                value={displayed}
                placeholder={placeholder ?? ""}
                onChange={(e) => onWriteField(name, e.target.value === "" ? null : e.target.value)}
            />
            <button
                type="button"
                className="password-mask__toggle"
                tabIndex={-1}
                aria-label={visible ? "Hide password" : "Show password"}
                onClick={() => setVisible((v) => !v)}
            >
                {visible ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
        </span>
    );
}

// `<separator string="…"/>` — standalone labeled divider. Reuses the
// section-heading visual: an optional uppercase accent label followed by a
// hairline rule that fills the remaining width. With no `string`, the label
// is omitted and the rule spans the full width (a plain divider).
function FormSeparator({ el }: { el: FormSeparatorElement }): ReactNode {
    return (
        <div className="form-separator">
            {el.string ? <span className="form-separator__label">{el.string}</span> : null}
            <span className="form-separator__rule" />
        </div>
    );
}

function FormSection({ el, ctx }: { el: FormSectionElement; ctx: RenderCtx }): ReactNode {
    const hasNestedSections = el.elements.some((c) => c.type === "section");

    const heading = el.string ? (
        <div className="form-section__heading">
            <span className="form-section__heading-label">{el.string}</span>
            <span className="form-section__heading-rule" />
        </div>
    ) : null;

    if (hasNestedSections) {
        // Grouping mode: sub-sections side-by-side. `cols` on each child
        // controls flex weight (cols=2 child gets 2× the share of a cols=1).
        return (
            <div className="form-section">
                {heading}
                <div className="form-section-group">
                    {el.elements.map((child, i) => {
                        if (child.type !== "section") {
                            return <FormElementRenderer key={i} el={child} ctx={ctx} inSection />;
                        }
                        const weight = (child as FormSectionElement).cols ?? 1;
                        return (
                            <div
                                key={i}
                                className={`form-section-group__col form-section-group__col--w${weight}`}
                            >
                                <FormSection el={child as FormSectionElement} ctx={ctx} />
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    }

    // Grid mode: fields flow into an N-column outer grid (`.form-grid`).
    // Default is a single label/value pair per row — fields stack
    // vertically and each row fills the section's available width.
    // Authors opt into multi-column layout via the DSL `cols=N` attribute.
    const cols = el.cols ?? 1;
    const gridClass = cols === 2
        ? "form-grid form-grid--cols-2"
        : cols === 3
            ? "form-grid form-grid--cols-3"
            : cols === 4
                ? "form-grid form-grid--cols-4"
                : "form-grid";

    return (
        <div className="form-section">
            {heading}
            <div className={gridClass}>
                {el.elements.map((child, i) => (
                    <FormElementRenderer key={i} el={child} ctx={ctx} inSection />
                ))}
            </div>
        </div>
    );
}

function FormNotebook({ el, ctx }: { el: FormNotebookElement; ctx: RenderCtx }): ReactNode {
    const visiblePages = el.pages.filter((p) => !isElementInvisible(p, ctx.draft));
    if (visiblePages.length === 0) return null;
    const initial = visiblePages[0].label;
    return <FormNotebookTabs pages={visiblePages} initial={initial} ctx={ctx} />;
}

function FormNotebookTabs({
    pages,
    initial,
    ctx,
}: {
    pages: FormNotebookPage[];
    initial: string;
    ctx: RenderCtx;
}): ReactNode {
    const [active, setActive] = useState(initial);
    return (
        <Tabs.Root className="form-tabs" value={active} onValueChange={setActive}>
            <Tabs.List className="form-tabs__list">
                {pages.map((page) => (
                    <Tabs.Trigger key={page.label} value={page.label} className="form-tabs__trigger">
                        {page.label}
                    </Tabs.Trigger>
                ))}
            </Tabs.List>
            {pages.map((page) => (
                <Tabs.Content key={page.label} value={page.label} className="form-tabs__panel">
                    {page.elements.map((child, i) => (
                        <FormElementRenderer key={i} el={child} ctx={ctx} />
                    ))}
                </Tabs.Content>
            ))}
        </Tabs.Root>
    );
}

function FormButton({ el, ctx }: { el: FormButtonElement; ctx: RenderCtx }): ReactNode {
    const dynamicLabel = el.labelField ? String(ctx.draft[el.labelField] ?? "") : "";
    const label = dynamicLabel || el.label || el.name;
    const Icon = el.icon ? appIcon(el.icon) : null;

    const signature = ctx.recordRef
        ? commandSignature({
              type: "RUN_BUTTON",
              payload: {
                  actionSlug: ctx.actionSlug,
                  recordUuid: ctx.recordRef,
                  commandName: el.command ?? "",
                  commandType: el.commandType ?? null,
              },
          })
        : "";
    const pending = usePendingCommand(signature);

    const onClick = async () => {
        if (!ctx.recordRef || pending) return;
        if (el.confirm) {
            const ok = await confirm({
                title: el.label || el.name,
                message: el.confirm,
                confirmLabel: el.label || "Confirm",
                cancelLabel: "Cancel",
            });
            if (!ok) return;
        }
        ctx.onRunButton?.(el);
    };

    return (
        <button
            type="button"
            className="form-action-button"
            onClick={onClick}
            disabled={pending || !ctx.recordRef}
            title={label}
        >
            {pending ? (
                <Loader2 size={13} className="form-action-button__spinner" />
            ) : Icon ? (
                <Icon size={13} />
            ) : null}
            <span>{label}</span>
        </button>
    );
}

// Many-to-one (Reference) picker — searchable combobox backed by the
// `/api/web/<actionSlug>/related/<field>` candidate endpoint via
// ActionService.searchRelated. Debounced refetch on input, returns the
// chosen {id, display_name} dict to onWriteField so the trigger stays in
// sync without an additional round-trip. buildSaveValues unpacks the id
// at save time.
function ReferenceCombobox({
    name,
    value,
    actionSlug,
    fieldName,
    onWriteField,
    placeholder,
    displayFormat,
    domainTemplate,
    draft,
}: {
    name: string;
    value: import("../core").Json;
    actionSlug: string;
    fieldName: string;
    onWriteField: (name: string, value: import("../core").Json) => void;
    placeholder: string;
    displayFormat?: string | null;
    domainTemplate?: string | null;
    draft: RecordData;
}): ReactNode {
    const runtime = useRuntime();
    const [open, setOpen] = useState(false);
    const [q, setQ] = useState("");
    const [items, setItems] = useState<RecordData[]>([]);
    const [loading, setLoading] = useState(false);

    // Resolve the current display label. Per-field `displayFormat` (XML
    // override) wins when the picked value carries the data fields it
    // references; otherwise fall back to the server-rendered display_name.
    const currentRecord = value && typeof value === "object" && !Array.isArray(value)
        ? (value as Record<string, unknown>)
        : null;
    const currentId =
        currentRecord ? (currentRecord.id == null ? null : String(currentRecord.id))
        : typeof value === "string" ? value
        : null;
    const currentLabel = renderRelatedLabel(currentRecord, displayFormat);

    // Resolve record-field references in the domain template at search time.
    // Both the explicit `$active_record.<field>` token and a bare field name
    // (e.g. `partner_id`) resolve to the live value of that field on the
    // current form draft, so the candidate list filters against sibling-field
    // selections the user has just made.
    const effectiveDomain = useMemo(() => {
        if (!domainTemplate) return null;
        return substituteDomainRecordTokens(domainTemplate, draft as Record<string, unknown>);
    }, [domainTemplate, draft]);

    // Debounced search when the popover is open. Cancel the prior timer
    // on each keystroke; clear on close so reopens start blank.
    useEffect(() => {
        if (!open) return;
        let alive = true;
        setLoading(true);
        const tid = setTimeout(() => {
            runtime.services.action.searchRelated(actionSlug, fieldName, q, { domain: effectiveDomain })
                .then((rows) => { if (alive) { setItems(rows); setLoading(false); } })
                .catch(() => { if (alive) { setItems([]); setLoading(false); } });
        }, 120);
        return () => { alive = false; clearTimeout(tid); };
    }, [open, q, actionSlug, fieldName, runtime, effectiveDomain]);

    return (
        <Popover.Root open={open} onOpenChange={setOpen}>
            <Popover.Trigger asChild>
                <button type="button" className="rdx-select__trigger" data-state={open ? "open" : "closed"}>
                    <span className="m2o__current">
                        {currentLabel
                            ? <span>{currentLabel}</span>
                            : currentId
                                ? <span className="cell-muted">{currentId.slice(0, 8)}…</span>
                                : <span className="cell-muted">{placeholder}</span>}
                    </span>
                    <ChevronDown size={14} className="rdx-select__icon" />
                </button>
            </Popover.Trigger>
            <Popover.Portal>
                <Popover.Content className="m2o__content" align="start" sideOffset={4} collisionPadding={12}>
                    <Command className="m2o__command" loop shouldFilter={false}>
                        <div className="m2o__search">
                            <Search size={14} className="m2o__search-icon" />
                            <Command.Input
                                autoFocus
                                value={q}
                                onValueChange={setQ}
                                placeholder="Search…"
                            />
                        </div>
                        <Command.List className="m2o__list">
                            {loading ? (
                                <div className="m2o__empty">
                                    <Loader2 size={14} className="form-toolbar__save-spinner" /> Loading…
                                </div>
                            ) : items.length === 0 ? (
                                <div className="m2o__empty">No records</div>
                            ) : null}
                            {!loading && currentId ? (
                                <Command.Item
                                    value="__clear__"
                                    className="m2o__item"
                                    onSelect={() => { onWriteField(name, null); setOpen(false); }}
                                >
                                    <span className="m2o__item-label cell-muted">Clear selection</span>
                                </Command.Item>
                            ) : null}
                            {!loading && items.map((row) => {
                                const id = String(row.id ?? "");
                                const lbl = renderRelatedLabel(row as Record<string, unknown>, displayFormat) || id;
                                // Keep the full row payload on the draft so a future re-render
                                // (or a subsequent open of the picker) can re-apply displayFormat
                                // without another /related round-trip.
                                const writeValue = { ...(row as Record<string, unknown>), id, display_name: lbl };
                                return (
                                    <Command.Item
                                        key={id}
                                        value={`${lbl} ${id}`}
                                        className="m2o__item"
                                        onSelect={() => {
                                            onWriteField(name, writeValue as import("../core").Json);
                                            setOpen(false);
                                        }}
                                    >
                                        <span className="m2o__item-label">{lbl}</span>
                                        {id === currentId ? <Check size={14} className="m2o__item-check" /> : null}
                                    </Command.Item>
                                );
                            })}
                        </Command.List>
                    </Command>
                </Popover.Content>
            </Popover.Portal>
        </Popover.Root>
    );
}

// Render the label for a related option — `displayFormat` (XML override)
// wins when present and the record carries the format-template keys.
// Otherwise falls back to the server-rendered `display_name`, then the
// raw id slice as last resort.
function renderRelatedLabel(record: Record<string, unknown> | null, displayFormat?: string | null): string {
    if (!record) return "";
    if (displayFormat) {
        const rendered = formatDisplay(displayFormat, record).trim();
        if (rendered) return rendered;
    }
    if (record.display_name != null) return String(record.display_name);
    return "";
}

// Many-to-many picker — multi-select cmdk + chip rail. Each chosen row
// renders as a chip with an `×` remove affordance; clicking the trigger
// opens a search popover for adding more. The draft value is a list of
// `{id, display_name, ...data}` records; buildSaveValues converts that
// to `[[6, 0, [ids]]]` (SET command tuple) at save time.
function M2MCombobox({
    name,
    value,
    actionSlug,
    fieldName,
    onWriteField,
    placeholder,
    displayFormat,
    domainTemplate,
    draft,
}: {
    name: string;
    value: import("../core").Json;
    actionSlug: string;
    fieldName: string;
    onWriteField: (name: string, value: import("../core").Json) => void;
    placeholder: string;
    displayFormat?: string | null;
    domainTemplate?: string | null;
    draft: RecordData;
}): ReactNode {
    const runtime = useRuntime();
    const [open, setOpen] = useState(false);
    const [q, setQ] = useState("");
    const [items, setItems] = useState<RecordData[]>([]);
    const [loading, setLoading] = useState(false);

    // Normalize current value → list of {id, display_name, ...}. The wire
    // shape can also be a list of command tuples ([[6,0,[ids]]] / [[4,id,0]])
    // mid-edit; those bypass the chip rail and round-trip verbatim.
    const selected: Array<Record<string, Json>> = useMemo(() => {
        if (!Array.isArray(value)) return [];
        return value
            .filter((item): item is Record<string, Json> =>
                item != null && typeof item === "object" && !Array.isArray(item))
            .map((item) => item);
    }, [value]);
    const selectedIds = useMemo(() => new Set(selected.map((r) => String(r.id))), [selected]);

    const effectiveDomain = useMemo(() => {
        if (!domainTemplate) return null;
        return substituteDomainRecordTokens(domainTemplate, draft as Record<string, unknown>);
    }, [domainTemplate, draft]);

    useEffect(() => {
        if (!open) return;
        let alive = true;
        setLoading(true);
        const tid = setTimeout(() => {
            runtime.services.action.searchRelated(actionSlug, fieldName, q, { domain: effectiveDomain })
                .then((rows) => { if (alive) { setItems(rows); setLoading(false); } })
                .catch(() => { if (alive) { setItems([]); setLoading(false); } });
        }, 120);
        return () => { alive = false; clearTimeout(tid); };
    }, [open, q, actionSlug, fieldName, runtime, effectiveDomain]);

    const onAdd = (row: Record<string, unknown>) => {
        const id = String(row.id ?? "");
        if (!id || selectedIds.has(id)) return;
        const lbl = renderRelatedLabel(row, displayFormat) || id;
        const next = [...selected, { ...row, id, display_name: lbl }];
        onWriteField(name, next as unknown as import("../core").Json);
    };
    const onRemove = (id: string) => {
        const next = selected.filter((r) => String(r.id) !== id);
        onWriteField(name, next as unknown as import("../core").Json);
    };

    return (
        <Popover.Root open={open} onOpenChange={setOpen}>
            <Popover.Trigger asChild>
                <button type="button" className="m2m__trigger rdx-select__trigger" data-state={open ? "open" : "closed"}>
                    <span className="m2m__chips">
                        {selected.length === 0 ? (
                            <span className="cell-muted">{placeholder}</span>
                        ) : selected.map((row) => {
                            const id = String(row.id ?? "");
                            const lbl = renderRelatedLabel(row, displayFormat) || id;
                            return (
                                <span
                                    key={id}
                                    className="m2m__chip form-tag"
                                    onClick={(e) => { e.stopPropagation(); onRemove(id); }}
                                    role="button"
                                    aria-label={`Remove ${lbl}`}
                                >
                                    <span>{lbl}</span>
                                    <X size={11} />
                                </span>
                            );
                        })}
                    </span>
                    <ChevronDown size={14} className="rdx-select__icon" />
                </button>
            </Popover.Trigger>
            <Popover.Portal>
                <Popover.Content className="m2o__content" align="start" sideOffset={4} collisionPadding={12}>
                    <Command className="m2o__command" loop shouldFilter={false}>
                        <div className="m2o__search">
                            <Search size={14} className="m2o__search-icon" />
                            <Command.Input
                                autoFocus
                                value={q}
                                onValueChange={setQ}
                                placeholder="Search…"
                            />
                        </div>
                        <Command.List className="m2o__list">
                            {loading ? (
                                <div className="m2o__empty">
                                    <Loader2 size={14} className="form-toolbar__save-spinner" /> Loading…
                                </div>
                            ) : items.length === 0 ? (
                                <div className="m2o__empty">No records</div>
                            ) : null}
                            {!loading && items.map((row) => {
                                const id = String(row.id ?? "");
                                const lbl = renderRelatedLabel(row as Record<string, unknown>, displayFormat) || id;
                                const isPicked = selectedIds.has(id);
                                return (
                                    <Command.Item
                                        key={id}
                                        value={`${lbl} ${id}`}
                                        className="m2o__item"
                                        onSelect={() => {
                                            if (isPicked) onRemove(id);
                                            else onAdd(row as Record<string, unknown>);
                                        }}
                                    >
                                        <span className="m2o__item-label">{lbl}</span>
                                        {isPicked ? <Check size={14} className="m2o__item-check" /> : null}
                                    </Command.Item>
                                );
                            })}
                        </Command.List>
                    </Command>
                </Popover.Content>
            </Popover.Portal>
        </Popover.Root>
    );
}

// Searchable enum dropdown — Radix Popover + cmdk filter, themed to match
// the m2o combobox chrome already in the SCSS. Closes after a pick; commits
// the new key via onWriteField. Empty option clears the field.
function EnumCombobox({
    name,
    value,
    options,
    onWriteField,
    placeholder,
}: {
    name: string;
    value: string | null;
    options: ReadonlyArray<readonly [string, string]>;
    onWriteField: (name: string, value: import("../core").Json) => void;
    placeholder: string;
}): ReactNode {
    const [open, setOpen] = useState(false);
    const current = options.find(([k]) => k === value) ?? null;
    const currentLabel = current ? current[1] : "";
    return (
        <Popover.Root open={open} onOpenChange={setOpen}>
            <Popover.Trigger asChild>
                <button type="button" className="rdx-select__trigger" data-state={open ? "open" : "closed"}>
                    <span className="m2o__current">
                        {currentLabel
                            ? <span>{currentLabel}</span>
                            : <span className="cell-muted">{placeholder}</span>}
                    </span>
                    <ChevronDown size={14} className="rdx-select__icon" />
                </button>
            </Popover.Trigger>
            <Popover.Portal>
                <Popover.Content className="m2o__content" align="start" sideOffset={4} collisionPadding={12}>
                    <Command className="m2o__command" loop>
                        <div className="m2o__search">
                            <Search size={14} className="m2o__search-icon" />
                            <Command.Input autoFocus placeholder="Search…" />
                        </div>
                        <Command.List className="m2o__list">
                            <Command.Empty className="m2o__empty">No records</Command.Empty>
                            {value != null ? (
                                <Command.Item
                                    value="__clear__"
                                    className="m2o__item"
                                    onSelect={() => { onWriteField(name, null); setOpen(false); }}
                                >
                                    <span className="m2o__item-label cell-muted">Clear selection</span>
                                </Command.Item>
                            ) : null}
                            {options.map(([key, lbl]) => (
                                <Command.Item
                                    key={key}
                                    value={`${lbl} ${key}`}
                                    className="m2o__item"
                                    onSelect={() => { onWriteField(name, key); setOpen(false); }}
                                >
                                    <span className="m2o__item-label">{lbl}</span>
                                    {key === value ? <Check size={14} className="m2o__item-check" /> : null}
                                </Command.Item>
                            ))}
                        </Command.List>
                    </Command>
                </Popover.Content>
            </Popover.Portal>
        </Popover.Root>
    );
}
